import Checkbox from '@alexis/components/Checkbox';
import { clsx } from '@alexis/helpers/clsx';
import { getCookie } from '@alexis/helpers/cookie';
import { toISOStringWithTimezone } from '@alexis/helpers/date';
import { genzoTrack, genzoTrackPageView } from '@alexis/helpers/genzo';
import { gtmTrack } from '@alexis/helpers/gtm';
import { generateCUID } from '@alexis/helpers/identity';
import { appendSearchParams } from '@alexis/helpers/searchParam';
import { useGetHotelPublicPaymentOption } from '@pages/HotelSearchResult/hooks/useGetPublicPaymentOption';
import axios, { CancelToken, CancelTokenSource } from 'axios';
import sha256 from 'crypto-js/sha256';
import { clamp } from 'lodash-es';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { NextObserver } from 'rxjs';

import { getGuestReviews, getHotel } from '@apis/hotel';
import { getHotelRoomTypes } from '@apis/hotelBooking';

import Carousel from '@components/Carousel';
import HotelAnchorTabs from '@components/hotels/HotelAnchorTabs';
import { STICKY_BAR_HEIGHT } from '@components/hotels/HotelAnchorTabs/HotelAnchorTabs';
import HotelDeals from '@components/hotels/HotelDeals';
import HotelDescription from '@components/hotels/HotelDescription';
import HotelMiniCard from '@components/hotels/HotelMiniCard';
import Price from '@components/Price';
import RefreshPopup from '@components/RefreshPopup';
import { ProductTypes } from '@components/RefreshPopup/RefreshPopup';

import { GTM_ID } from '@constants/general';
import { ENHANCED_HOTEL_RATES } from '@constants/hotels';

import { GlobalContext } from '@context/GlobalContext';

import { notUndefinedAndNull } from '@helpers/array';
import { injectGoogleMapScript } from '@helpers/googleMap';
import {
  convertGuestRoomsToSearchParam,
  convertGuestSearchParamToGuestRooms,
  shouldChangePriceOptions,
  sortHotelRates,
} from '@helpers/hotel';
import { convertHotelRatePriceToPrice, getBestDealUsualPrice } from '@helpers/price';
import { hotelsRoute } from '@helpers/routeTranslation';
import {
  webEngageTrackHotelSearch,
  webEngageTrackHotelView,
  webEngageTrackPurchase,
  webEngageTrackRateClick,
} from '@helpers/webEngage';

import OpenInNewIcon from '@icons/open_in_new.svg';
import StarIcon from '@icons/star_rounded.svg';

import { useFbpFbc } from '@hooks/useFbpFbc';
import { useHotelMetaData } from '@hooks/useHotelMetaData';
import { useIntersectionObserver } from '@hooks/useIntersectionObserver';

import { hotelSearchObservable } from '@observables/hotelObservables';
import { hotelRateObservable } from '@observables/hotelRateObservables';

import { changeHotelMetaSearch } from '@redux/actions/hotelMetaSearchActions';
import { changeHomePageViewId } from '@redux/actions/pageViewIdActions';
import {
  changeIsIncludingTaxOrFee,
  changeShowTotalPrice,
} from '@redux/actions/priceOptionsActions';
import {
  getCurrencyState,
  getCurrentSiteState,
  getExchangeRateState,
  getHotelMetaSearchState,
  getIsRtlState,
  getLocaleState,
  getPriceOptionsState,
  getTranslationsState,
  getUserState,
} from '@redux/selectors';

import { Currency } from '@wegoTypes/currency';
import type { CurrentSite } from '@wegoTypes/currentSite';
import type { HotelBookingRoomTypes } from '@wegoTypes/hotelBooking/hotelRoomTypes';
import { Hotel } from '@wegoTypes/hotels/hotel';
import { HotelGuestReview } from '@wegoTypes/hotels/hotelGuestReview';
import { HotelMetaSearch } from '@wegoTypes/hotels/hotelMetaSearch';
import { HotelReview } from '@wegoTypes/hotels/hotelReview';
import { HotelRateAmenity } from '@wegoTypes/hotels/metasearch/hotelRateAmenity';
import { HotelRateResult } from '@wegoTypes/hotels/metasearch/hotelRateResult';
import { HotelSearchResult } from '@wegoTypes/hotels/metasearch/hotelSearchResult';
import type { HotelSearchResultSearch } from '@wegoTypes/hotels/metasearch/hotelSearchResultSearch';
import type { MetasearchHotelRate } from '@wegoTypes/hotels/metasearch/metasearchHotelRate';
import { MetasearchHotelRatePrice } from '@wegoTypes/hotels/metasearch/metasearchHotelRatePrice';
import { Room } from '@wegoTypes/hotels/metasearch/room';
import { SimilarHotel } from '@wegoTypes/hotels/metasearch/similarHotel';
import { SimilarHotelViewModel } from '@wegoTypes/hotels/similarHotelViewModel';
import { HotelDetailGtmPageData, HotelGtmSkycannerTopDeal } from '@wegoTypes/tracking/gtm/hotel';
import { Translations } from '@wegoTypes/translations';
import type { User } from '@wegoTypes/user';

import AiReviews from './components/AiReviews';
import Breadcrumbs from './components/Breadcrumbs';
import ChooseYourRoom from './components/ChooseYourRoom';
import GuestReviews from './components/GuestReview/GuestReviews';
import HotelInformation from './components/HotelInformation';
import HotelStickyRatesFilter from './components/HotelStickyRatesFilter';
import styles from './HotelDetail.module.scss';

interface HotelDetailProps {
  gtmAuth: string;
  gtmPreview: string;
  additionalUrlTrackingParams?: { [key: string]: string | number };
  localeParam?: string;
  nearestCityByIP?: Place;
  wegoAnalyticsClientId?: string;
  wegoAnalyticsClientSessionId?: string;
}

const HotelDetail: React.FC<HotelDetailProps> = ({
  additionalUrlTrackingParams,
  gtmAuth,
  gtmPreview,
  localeParam,
  nearestCityByIP,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
}): JSX.Element => {
  const apiBaseUrl: string = API_BASE_URL;
  const currency: Currency = useSelector(getCurrencyState);
  const currentSite: CurrentSite = useSelector(getCurrentSiteState);
  const exchangeRate: ExchangeRate = useSelector(getExchangeRateState);
  const hotelMetaSearch: HotelMetaSearch = useSelector(getHotelMetaSearchState);
  const isRtl: boolean = useSelector(getIsRtlState);
  const locale: string = useSelector(getLocaleState);
  const priceOptions: PriceOptions = useSelector(getPriceOptionsState);
  const translations: Translations = useSelector(getTranslationsState);
  const user: User | null = useSelector(getUserState);

  const { webEngageAnonymousId, userCentricCoreConfigs } = useContext(GlobalContext);

  // Get public payment options
  const { data: paymentOptions } = useGetHotelPublicPaymentOption(
    {
      clientCode: 'bow_hot',
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const roomSelectionRef = useRef<HTMLDivElement>(null);

  const [isWebEngageHotelViewTracked, setIsWebEngageHotelViewTracked] = useState<boolean>(false);
  const [isWebEngageHotelSearchTracked, setIsWebEngageHotelSearchTracked] = useState<boolean>(true);

  const [isLoadingRates, setIsLoadingRates] = useState<boolean>(true);

  const [hotel, setHotel] = useState<Hotel>();
  const [isGroupSearch, setIsGroupSearch] = useState<boolean>(false);
  const [rates, setRates] = useState<Array<MetasearchHotelRate>>([]);
  const [rateAmenities, setRateAmenities] = useState<Array<HotelRateAmenity>>([]);
  const [roomMap, setRoomMap] = useState<{ [key: string]: Room }>({});
  const [similarHotelViewModels, setSimilarHotelViewModels] = useState<
    Array<SimilarHotelViewModel>
  >([]);

  const [bestDeal, setBestDeal] = useState<MetasearchHotelRate>();
  const [allReviewerGroupReview, setAllReviewerGroupReview] = useState<HotelReview>();

  const [mapDivRef, isMapDivVisible] = useIntersectionObserver<HTMLDivElement>();

  const [hotelBookingRoomTypes, setHotelBookingRoomTypes] = useState<HotelBookingRoomTypes>();

  const [isSearchExpired, setIsSearchExpired] = useState(false);

  const [reviews, setReviews] = React.useState<HotelGuestReview>({} as HotelGuestReview);
  const [reviewLocale, setReviewLocale] = React.useState(locale);
  const [isOpenMapPopup, setIsOpenMapPopup] = React.useState(false);

  const dispatch = useDispatch();

  const { pathname, search } = useLocation();
  const locationState = useLocation().state as {
    impressionType: 'filter' | 'page_load';
  };

  const navigate = useNavigate();

  const params = useParams<{
    checkInDate: string;
    checkOutDate: string;
    guests: string;
    hotelId: string;
  }>();

  const [searchParams] = useSearchParams();
  const { fbp, fbc } = useFbpFbc();
  const { title } = useHotelMetaData();

  const isEnhancedHotelRates =
    userCentricCoreConfigs.find((coreConfig) => coreConfig.key === ENHANCED_HOTEL_RATES)?.value ===
    '1';

  //#region Hotel params
  const searchIdParam = searchParams.get('search_id');
  const guestsSearchParam = searchParams.get('guests');

  const bookingOptionsSearchParam = searchParams.get('providers');
  const rateAmenityIdsSearchParam = searchParams.get('free_amenities');
  const providerCodesSearchParam = searchParams.get('provider_codes');
  const providerSearchParam = searchParams.get('provider');
  const wegoHotelIdSearchParam = searchParams.get('wego_hotel_id');
  //#endregion

  const wegoInternalCampaignSearchParam = searchParams.get('wg_internal_campaign');
  const isCrossSell = wegoInternalCampaignSearchParam === 'hotels_crosssell';

  const bowOnlySearchParam = searchParams.get('bow_only');
  const tsCodeSearchParam = searchParams.get('ts_code');

  const wegoSourceSearchParam = searchParams.get('wg_source');
  const wegoCampaignSearchParam = searchParams.get('wg_campaign');
  const wegoMediumSearchParam = searchParams.get('wg_medium');

  // Google hotel encrypted wego price
  const wegoPriceSearchParam = searchParams.get('wego_price');
  const wegoGrowthSearchParam = searchParams.get('wego_growth');
  const ghSearchParam = searchParams.get('gh_params');

  const pageViewId = useMemo<string>(() => {
    return generateCUID();
  }, []);

  // Remove all filter params from search
  const searchWithoutExtraParams = useMemo<string>(() => {
    const searchParams = new URLSearchParams(search);

    searchParams.delete('modal');
    searchParams.delete('search_id');
    searchParams.delete('providers');
    searchParams.delete('free_amenities');
    searchParams.delete('provider_codes');
    searchParams.delete('open_calendar');
    searchParams.delete('wego_hotel_id');
    searchParams.delete('wego_price');
    searchParams.delete('provider');
    searchParams.delete('wego_growth');

    const updatedSearchParams = searchParams.toString();

    return !!updatedSearchParams ? `?${updatedSearchParams}` : '';
  }, [search]);

  const hasTabbyGateway = useMemo<boolean>(() => {
    return !!paymentOptions?.find((paymentOption) => paymentOption.channel === 'BNPL');
  }, [paymentOptions]);

  // Retrieve booking options filter from search param
  const selectedBookingOptions = useMemo<Array<string>>(() => {
    if (!!bookingOptionsSearchParam) {
      return bookingOptionsSearchParam.split(',');
    }
    return [];
  }, [bookingOptionsSearchParam]);

  // Retrieve rate provider codes filter from search param
  const selectedRateProviderCodes = useMemo<Array<string>>(() => {
    if (!!providerCodesSearchParam) {
      return providerCodesSearchParam.split(',');
    }
    return [];
  }, [providerCodesSearchParam]);

  // Filter rates by booking options and rate provider codes (To display available rate amenities filter within these filtered rates)
  const filteredRatesByBookingOptionsAndRateProviderCodes = useMemo<
    Array<MetasearchHotelRate>
  >(() => {
    return rates.filter((rate) => {
      const bookingOptionsFilter =
        selectedBookingOptions.length > 0
          ? selectedBookingOptions.some(
              (selectedBookingOption) =>
                (selectedBookingOption === 'wego' && rate.provider.directBooking) ||
                (selectedBookingOption === 'hotels' && rate.provider.isHotelWebsite) ||
                (selectedBookingOption === 'ota' && rate.provider.type === 'OTA'),
            )
          : true;

      const rateProviderCodesFilter =
        selectedRateProviderCodes.length > 0
          ? selectedRateProviderCodes.some(
              (selectedRateProviderCode) =>
                rate.provider.code.toLowerCase() === selectedRateProviderCode.toLowerCase(),
            )
          : true;

      return bookingOptionsFilter && rateProviderCodesFilter;
    });
  }, [rates, selectedBookingOptions, selectedRateProviderCodes]);

  // Filter rate amenities based on filtered rates by booking options and rate provider codes
  const filteredRateAmenities = useMemo<Array<HotelRateAmenity>>(() => {
    const filteredRateAmenities = rateAmenities.filter((rateAmenity) => {
      return (
        filteredRatesByBookingOptionsAndRateProviderCodes.some((rate) =>
          rate.rateAmenityIds.includes(rateAmenity.id),
        ) && rateAmenity.code !== 'NO_CREDIT_CARD_NEEDED'
      );
    });

    // Create a shopcash cashback rate amenity on the front-end with id:10000
    if (
      filteredRatesByBookingOptionsAndRateProviderCodes.some(
        (rate) => !!rate.params?.cashback_percentage,
      )
    ) {
      filteredRateAmenities.push({
        id: 10000,
        code: 'CASHBACK',
        type: 'SHOPCASH',
        name: translations.cashback as string,
      });
    }

    if (hasTabbyGateway) {
      const hasRateWithoutPayAtTheHotelOrPayLaterRateAmenity =
        filteredRatesByBookingOptionsAndRateProviderCodes.some((rate) => {
          const rateAmenities$ = rate.rateAmenityIds
            .map((rateAmenityId) =>
              rateAmenities.find((rateAmenity) => rateAmenityId === rateAmenity.id),
            )
            .filter(notUndefinedAndNull);

          return (
            rate.provider.directBooking &&
            !rateAmenities$.some(
              (rateAmenity) =>
                rateAmenity.code === 'PAY_AT_THE_HOTEL' || rateAmenity.code === 'PAY_LATER',
            )
          );
        });

      // Create a BNPL rate amenity on the front-end with id:10001
      if (hasRateWithoutPayAtTheHotelOrPayLaterRateAmenity) {
        filteredRateAmenities.push({
          id: 10001,
          code: 'BUY_NOW_PAY_LATER',
          type: 'PAYMENT',
          name: translations.lbl_interest_free_payments as string,
        });
      }
    }

    return filteredRateAmenities;
  }, [rateAmenities, filteredRatesByBookingOptionsAndRateProviderCodes, hasTabbyGateway]);

  // Retrieve rate amenity Ids filter from search param
  const selectedRateAmenityIds = useMemo<Array<number>>(() => {
    if (!!rateAmenityIdsSearchParam) {
      return rateAmenityIdsSearchParam.split(',').map((rateAmenityId) => parseInt(rateAmenityId));
    }
    return [];
  }, [rateAmenityIdsSearchParam]);

  // Filter rates by rate amenity Ids
  const filteredRates = useMemo<Array<MetasearchHotelRate>>(() => {
    return filterByRateAmenityIds(filteredRatesByBookingOptionsAndRateProviderCodes);
  }, [filteredRatesByBookingOptionsAndRateProviderCodes, selectedRateAmenityIds]);

  const bestDealUsualPrice = getBestDealUsualPrice(bestDeal, priceOptions);

  useEffect(() => {
    if (!pageViewId) return;

    dispatch(changeHomePageViewId(pageViewId));
  }, [pageViewId]);

  // Redirect with search param if any one of them is missing.
  useEffect(() => {
    if (!guestsSearchParam) {
      const searchParams = new URLSearchParams(search);
      searchParams.append(
        'guests',
        convertGuestRoomsToSearchParam([{ adultsCount: 2, childrenCount: 0, childrenAges: [] }]),
      );

      navigate(
        { pathname, search: searchParams.toString() },
        { replace: true, state: locationState },
      );
    }
  }, [pathname, locationState]);

  // WebEngage Hotel View
  useEffect(() => {
    if (
      !isWebEngageHotelViewTracked &&
      !!hotelMetaSearch.search?.id &&
      !!nearestCityByIP &&
      !!hotel &&
      filteredRates.length > 0
    ) {
      webEngageTrackHotelView(
        currentSite.countryCode,
        locale,
        nearestCityByIP,
        hotelMetaSearch.search,
        window.location.href,
        hotel,
        filteredRates,
      );
      setIsWebEngageHotelViewTracked(true);
    }
  }, [hotelMetaSearch.search?.id, nearestCityByIP, hotel, filteredRates]);

  // WebEngage Hotel Search
  useEffect(() => {
    if (!isWebEngageHotelSearchTracked && !!hotelMetaSearch.search?.id && !!nearestCityByIP) {
      webEngageTrackHotelSearch(
        currentSite.countryCode,
        locale,
        nearestCityByIP,
        hotelMetaSearch.search,
        window.location.href,
      );
      setIsWebEngageHotelSearchTracked(true);
    }
  }, [isWebEngageHotelSearchTracked, hotelMetaSearch.search?.id, nearestCityByIP]);

  // Genzo Tracking
  useEffect(() => {
    if (
      !!hotelMetaSearch.search?.id &&
      !!hotel &&
      !!wegoAnalyticsClientId &&
      !!wegoAnalyticsClientSessionId
    ) {
      const { country, city, region } = hotelMetaSearch.search;
      const { id, name } = hotel;

      const clientData: any = {
        id: wegoAnalyticsClientId,
        session_id: wegoAnalyticsClientSessionId,
        ...(!!user && {
          user_hash: user.userHash,
        }),
      };

      const pageData: any = {
        name: `Hotels Details|${country.name}|${!!city ? city.name : ''}${
          !!region ? region.name : ''
        }|${id}|${name}, ${!!city ? city.name : ''}${!!region ? region.name : ''}`,
        product: 'hotels',
        base_type: 'hotels_detail_page_live',
        sub_type: 'hotels_detail_page_live',
        locale: locale,
        site_code: currentSite.countryCode,
        url: window.location.href,
        referrer_url: document.referrer,
      };

      const data: any = {
        id: pageViewId,
        client: clientData,
        event_id: id.toString(),
        page: pageData,
        created_at: toISOStringWithTimezone(new Date()),
        external_services: {
          crm_id: !!user ? user.userHash : webEngageAnonymousId,
          ...(!!fbp && { fbp }),
          ...(!!fbc && { fbc }),
        },
      };

      genzoTrackPageView(apiBaseUrl, data);
    }
  }, [
    hotelMetaSearch.search?.id,
    hotel,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
    webEngageAnonymousId,
  ]);

  // Impression Log: when page load, update history state
  useEffect(() => {
    navigate(
      { pathname, search: searchParams.toString() },
      {
        replace: true,
        state: {
          ...locationState,
          impressionType: 'page_load',
        },
      },
    );
  }, [hotelMetaSearch.search?.id]);

  // Todo: Remove old genzo visits
  useEffect(() => {
    if (
      !!hotelMetaSearch.search?.id &&
      !!hotel &&
      !!wegoAnalyticsClientId &&
      !!wegoAnalyticsClientSessionId
    ) {
      const { country, city, region } = hotelMetaSearch.search;
      const { id, name } = hotel;

      const visitData: any = {
        id: pageViewId,
        client: {
          app_type: 'WEB_APP',
          resolution: window.screen.availHeight + 'x' + window.screen.availWidth,
          id: wegoAnalyticsClientId,
          session_id: wegoAnalyticsClientSessionId,
          ...(!!user && { user_hash: user.userHash }),
        },
        campaign: { source: wegoSourceSearchParam, ts_code: getCookie('wego_ts_code') ?? null },
        page: {
          locale: locale,
          site_code: currentSite.countryCode,
          url: window.location.href,
          referrer_url: document.referrer,
          name: `Hotels Details|${country.name}|${!!city ? city.name : ''}${
            !!region ? region.name : ''
          }|${id}|${name}, ${!!city ? city.name : ''}${!!region ? region.name : ''}`,
          base_type: 'hotels_detail_page_live',
        },
        created_at: new Date().toISOString(),
        external_service: {
          crm_id: !!user ? user.userHash : webEngageAnonymousId,
          ...(!!fbp && { fbp }),
          ...(!!fbc && { fbc }),
        },
        ...(!!user && {
          user: {
            email: user.email,
          },
        }),
      };

      genzoTrack(`${apiBaseUrl}/genzo/v2/visits`, visitData);
    }
  }, [
    hotelMetaSearch.search?.id,
    hotel,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
    webEngageAnonymousId,
  ]);

  // Gtm Tracking
  useEffect(() => {
    if (
      !!hotel &&
      !!hotelMetaSearch.search &&
      !!wegoAnalyticsClientId &&
      !!wegoAnalyticsClientSessionId &&
      !!nearestCityByIP
    ) {
      const { id, name, star, city, reviews } = hotel;
      const reviewsAll = !!reviews ? reviews[0] : undefined;
      const pageData: HotelDetailGtmPageData = {
        check_in: params.checkInDate as string,
        check_out: params.checkOutDate as string,
        client_type: 'WEB_APP',
        currency_code: currency.code,
        domain: window.location.hostname,
        event: 'pageChanged',
        guest_count: hotelMetaSearch.search.guestsCount,
        hotel_id: id,
        hotel_name: name,
        hotel_review_score: !!reviewsAll ? reviewsAll.score : '',
        hotel_star: star,
        locale: locale,
        page_name: `Hotels Details|${city.countryName}|${city.name}|${id}|${name}, ${city.name}`,
        page_type: 'hotels_detail_page_live',
        product: 'hotels',
        rooms_count: hotelMetaSearch.search.roomsCount,
        session_id: wegoAnalyticsClientSessionId,
        site_code: currentSite.countryCode,
        ts_code: getCookie('wego_ts_code') ?? null,
        url: window.location.href,
        user_country_code: nearestCityByIP.countryCode,
        user_id: getCookie('wego_user_id') ?? null,
        ...(!!user?.email && { e_id: sha256(user?.email).toString() }),
      };

      gtmTrack(GTM_ID, gtmAuth, gtmPreview, pageData);
    }
  }, [
    locale,
    hotel,
    hotelMetaSearch.search?.id,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
    nearestCityByIP,
    user,
  ]);

  // Sent GTM skycanner for top rate
  const previousPriceRef = useRef<number | undefined | null>(null);
  useEffect(() => {
    let gtmSkycanner: HotelGtmSkycannerTopDeal = {
      display_price: null,
      display_price_tax_inclusive: priceOptions.isIncludingTaxOrFee,
      display_price_type: priceOptions.showTotalPrice ? 'total_stay' : 'per_night',
      event: 'topDeal',
      fees: null,
      taxes: null,
      total_price: null,
    };

    if (isLoadingRates === false && previousPriceRef === undefined && bestDeal === undefined) {
      gtmTrack(GTM_ID, gtmAuth, gtmPreview, gtmSkycanner);
    }

    const currentTopPriceAmount = bestDeal?.price.amount;
    if (filteredRates.length > 0 && currentTopPriceAmount !== previousPriceRef.current) {
      const bestDealPrice = convertHotelRatePriceToPrice(bestDeal!.price, true, true);
      gtmSkycanner.display_price = convertHotelRatePriceToPrice(
        bestDeal!.price,
        priceOptions.showTotalPrice,
        priceOptions.isIncludingTaxOrFee,
      ).amount;
      gtmSkycanner.fees = clamp(bestDeal!.price.totalLocalTaxAmount, 0, Infinity);
      gtmSkycanner.taxes = bestDeal!.price.totalTaxAmount;
      gtmSkycanner.total_price = bestDealPrice.amount;
      gtmTrack(GTM_ID, gtmAuth, gtmPreview, gtmSkycanner);
    }
    previousPriceRef.current = currentTopPriceAmount;
  }, [bestDeal, priceOptions, isLoadingRates]);

  // Get hotel detail
  useEffect(() => {
    const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

    const initHotel = async (
      hotelId: number,
      locale: string,
      cancelToken: CancelToken,
    ): Promise<void> => {
      try {
        const hotel = await getHotel(apiBaseUrl, hotelId, locale, cancelToken);
        setHotel(hotel);
      } catch (error: any) {
        if (error.response) {
          if (error.response.status === 404) {
            // redirect to search result if hotelId is not found
            navigate(
              { pathname: pathname.replace(`/${hotelId}`, ''), search: searchParams.toString() },
              { replace: true, state: locationState },
            );
          }
        }
      }
    };

    initHotel(parseInt(params.hotelId!), locale, cancelTokenSource.token);

    return () => {
      cancelTokenSource.cancel('Component unmount.');
    };
  }, [params.hotelId, locale]);

  // Trigger hotel metasearch.
  useEffect(() => {
    if (!searchIdParam && !!guestsSearchParam) {
      const { checkInDate, checkOutDate, hotelId } = params;
      let isParamsValid = true;

      if (!/^(?:[0-9]{4}\-[0-9]{2}\-[0-9]{2})$/.test(checkInDate!)) {
        isParamsValid = false;
      }

      if (!/^(?:[0-9]{4}\-[0-9]{2}\-[0-9]{2})$/.test(checkOutDate!)) {
        isParamsValid = false;
      }

      if (isParamsValid) {
        const extraBodyParams: { [key: string]: string | boolean } = {
          ...(bowOnlySearchParam === 'true' && { bowOnly: true }),
        };

        const extraSearchBodyParams: {
          [key: string]: string | { [key: string]: string } | boolean;
        } = {
          ...(!!wegoInternalCampaignSearchParam && isCrossSell && { crossSell: true }),
          ...(tsCodeSearchParam !== null && { tsCode: tsCodeSearchParam }),
          ...(!!wegoPriceSearchParam && {
            encryptedDisplayedPriceByHotelId: { [`${hotelId}`]: wegoPriceSearchParam },
          }),
          ...(wegoInternalCampaignSearchParam && {
            wgInternalCampaign: wegoInternalCampaignSearchParam,
          }),
          ...(wegoCampaignSearchParam && { wgCampaign: wegoCampaignSearchParam }),
          ...(wegoSourceSearchParam && { wgSource: wegoSourceSearchParam }),
          ...(wegoMediumSearchParam && { wgMedium: wegoMediumSearchParam }),
          ...(wegoGrowthSearchParam && { wgGrowth: 'dweb_cross_sell' }),
          ...(ghSearchParam && { ghParams: ghSearchParam }),
          ...(providerSearchParam && { ghProviderCode: providerSearchParam }),
        };

        const guestRooms = convertGuestSearchParamToGuestRooms(guestsSearchParam!);

        const hotelSearchHotelObservable$ = hotelSearchObservable({
          url: `${apiBaseUrl}/v3/metasearch/hotels/${hotelId}/searches`,
          user,
          search: {
            siteCode: currentSite.countryCode,
            locale,
            currencyCode: currency.code,
            checkIn: checkInDate!,
            checkOut: checkOutDate!,
            rooms: guestRooms,
            hotelId: parseInt(hotelId!),
          },
          clientId: wegoAnalyticsClientId,
          sessionId: wegoAnalyticsClientSessionId,
          similarHotelsLimit: 10,
          extraSearchBodyParams,
          extraBodyParams,
        });

        setIsSearchExpired(false);

        const hotelSearchObserver = (): NextObserver<HotelSearchResult> => {
          return {
            next: (hotelSearchResult: HotelSearchResult) => {
              const searchParams = new URLSearchParams(search);
              searchParams.append('search_id', hotelSearchResult.search.id);

              navigate(
                { pathname, search: searchParams.toString() },
                { replace: true, state: locationState },
              );

              triggerGenzoSearchesTrack(hotelSearchResult.search);
            },
            complete: () => {
              console.log('done');
            },
            error: (err) => console.log(err),
          };
        };

        const subscription = hotelSearchHotelObservable$.subscribe(hotelSearchObserver());

        return () => {
          subscription.unsubscribe();
        };
      }
    }
  }, [params.hotelId, searchIdParam, guestsSearchParam]);

  // Trigger hotel rate metasearch.
  useEffect(() => {
    if (!!searchIdParam && !!hotel) {
      const isGoogleHotel =
        !!wegoHotelIdSearchParam &&
        !!providerSearchParam &&
        window.atob(providerSearchParam) === 'agoda.com' &&
        tsCodeSearchParam === '2f2fc';

      const hotelRateObservable$ = hotelRateObservable(
        `${apiBaseUrl}/v3/metasearch/hotels`,
        parseInt(params.hotelId!),
        searchIdParam,
        locale,
        currency.code,
        'NIGHTLY',
        wegoAnalyticsClientId,
        wegoAnalyticsClientSessionId,
        10,
        isGoogleHotel,
      );

      setIsLoadingRates(true);

      const hotelRateObserver = (): NextObserver<HotelRateResult> => {
        return {
          next: ({
            groupSearch,
            rateAmenities,
            rates,
            rooms,
            search,
            similarHotels,
            similarHotelsRateAmenities,
            priceDisplaying,
          }) => {
            const hotelSearch: HotelSearchResultSearch = {
              ...search,
              city: {
                code: hotel.city.code,
                name: hotel.city.name,
              },
              cityCode: hotel.city.code,
              cityName: hotel.city.name,
              country: { code: hotel.city.countryCode, name: hotel.city.countryName },
              countryCode: hotel.city.countryCode,
              countryName: hotel.city.countryName,
              hotel: { id: hotel.id, name: hotel.name },
              searchType: 'HOTEL',
            };
            shouldChangePriceOptions(dispatch, priceDisplaying, currentSite.countryCode);
            dispatch(changeHotelMetaSearch(hotelSearch));

            setIsGroupSearch(groupSearch || false);

            setRates(
              sortHotelRates(
                rates,
                'asc',
                priceOptions,
                providerSearchParam,
                wegoHotelIdSearchParam,
              ),
            );
            setRateAmenities(
              Object.keys(rateAmenities).map<HotelRateAmenity>((key) => rateAmenities[Number(key)]),
            );
            setRoomMap(rooms || {});

            const similarHotelRateAmenities = Object.keys(
              similarHotelsRateAmenities,
            ).map<HotelRateAmenity>((key) => similarHotelsRateAmenities[Number(key)]);

            const similarHotelViewModels = similarHotels.map<SimilarHotelViewModel>(
              (similarHotel) => {
                let rateAmenities: Array<HotelRateAmenity> = [];

                if (!!similarHotel.bestRate?.rateAmenityIds) {
                  rateAmenities = similarHotel.bestRate.rateAmenityIds
                    .map((rateAmenityId: number) =>
                      similarHotelRateAmenities.find(
                        (rateAmenity) => rateAmenityId === rateAmenity.id,
                      ),
                    )
                    .filter(notUndefinedAndNull)
                    .sort((a: HotelRateAmenity, b: HotelRateAmenity) => a.id - b.id);
                }

                return {
                  hotel: similarHotel,
                  rateAmenities: rateAmenities,
                };
              },
            );

            setSimilarHotelViewModels(similarHotelViewModels);
          },
          complete: () => {
            setIsLoadingRates(false);
          },
          error: () => {
            setIsLoadingRates(false);

            const searchParams = new URLSearchParams(search);
            searchParams.delete('search_id');

            navigate(
              { pathname, search: searchParams.toString() },
              { replace: true, state: locationState },
            );
          },
        };
      };

      setIsWebEngageHotelSearchTracked(false);

      const subscription = hotelRateObservable$.subscribe(hotelRateObserver());

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [
    params.hotelId,
    searchIdParam,
    guestsSearchParam,
    hotel,
    wegoHotelIdSearchParam,
    providerSearchParam,
    tsCodeSearchParam,
  ]);

  // Initialize Room Types
  useEffect(() => {
    if (!!params.hotelId && rates.length > 0) {
      const firstRate = rates[0];

      const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

      const initHotelRoomTypes = async (
        cancelToken: CancelToken,
        hotelId: number,
        metasearchHotelRateParamsSessionId: string,
      ): Promise<void> => {
        try {
          const hotelRoomTypes = await getHotelRoomTypes(
            apiBaseUrl,
            cancelToken,
            hotelId,
            locale,
            metasearchHotelRateParamsSessionId,
            hotelMetaSearch.search!.id,
            wegoAnalyticsClientId!,
            wegoAnalyticsClientSessionId!,
          );

          setHotelBookingRoomTypes(hotelRoomTypes);
        } catch (_) {}
      };

      initHotelRoomTypes(
        cancelTokenSource.token,
        parseInt(params.hotelId),
        firstRate.params.session_id,
      );

      return () => {
        cancelTokenSource.cancel('Component unmount.');
      };
    }
  }, [params.hotelId, rates.length, locale]);

  // Get best deal rate
  useEffect(() => {
    const providerCode = decodeProviderSearchParam(providerSearchParam);
    const filteredRatesWithProvider = filterRatesByProvider(
      filteredRates,
      providerCode,
      wegoHotelIdSearchParam,
    );
    const bestDeal = determineBestDeal(filteredRates, filteredRatesWithProvider, priceOptions);
    setBestDeal(bestDeal);
  }, [filteredRates, providerSearchParam, priceOptions]);

  // Get all review from hotel
  useEffect(() => {
    if (!!hotel) {
      if (!!hotel.reviews && hotel.reviews.length > 0) {
        setAllReviewerGroupReview(hotel.reviews[0]);
      } else {
        setAllReviewerGroupReview(undefined);
      }
    }
  }, [hotel]);

  // Initialize google map
  useEffect(() => {
    if (isMapDivVisible && !!hotel?.latitude && !!hotel?.longitude) {
      const initializeGoogleMap = async (locale: string): Promise<void> => {
        await injectGoogleMapScript(locale);

        const googleMapLatLngLiteral: google.maps.LatLngLiteral = {
          lat: hotel.latitude,
          lng: hotel.longitude,
        };

        const mapOptions: google.maps.MapOptions = {
          center: googleMapLatLngLiteral,
          keyboardShortcuts: false,
          mapTypeControl: false,
          zoom: 17,
          zoomControl: true,
        };

        const map = new google.maps.Map(mapDivRef.current!, mapOptions);

        new google.maps.Marker({ position: googleMapLatLngLiteral, map });
      };

      initializeGoogleMap(locale);
    }
  }, [isMapDivVisible, hotel]);

  // Expire search dialog timeout
  useEffect(() => {
    if (!!hotelMetaSearch.search?.createdAt) {
      const hotelMetaSearchExpireByMilliseconds =
        new Date(hotelMetaSearch.search.createdAt).getTime() + 1800 * 1000; // 1,800,000 milliseconds = 30mins
      const nowMilliseconds = Date.now();

      if (hotelMetaSearchExpireByMilliseconds > nowMilliseconds) {
        const refreshSearchTimeout = setTimeout((): void => {
          setIsSearchExpired(true);
        }, hotelMetaSearchExpireByMilliseconds - nowMilliseconds);

        return () => {
          clearTimeout(refreshSearchTimeout);
        };
      }
    }
  }, [hotelMetaSearch.search?.createdAt]);

  // Get guest reviews
  useEffect(() => {
    if (params.hotelId) {
      const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
      let hasRefetched = false;

      const initGuestReviews = async (hotelId: number, cancelToken: CancelToken): Promise<void> => {
        try {
          const guestReviews = await getGuestReviews(
            apiBaseUrl,
            hotelId,
            { locale, per_page: 5, page: 1, sorts: ['posted_at,desc', 'rating,desc'] },
            cancelToken,
          );

          if (guestReviews.data.length === 0 && locale !== 'en' && !hasRefetched) {
            // Refetch with locale 'en' if data is empty and locale is not 'en', and hasn't refetched yet
            hasRefetched = true;
            const fallbackReviews = await getGuestReviews(
              apiBaseUrl,
              hotelId,
              { locale: 'en', per_page: 5, page: 1, sorts: ['posted_at,desc', 'rating,desc'] },
              cancelToken,
            );
            setReviewLocale('en');
            setReviews(fallbackReviews);
          } else {
            setReviews(guestReviews);
          }
        } catch (_) {}
      };

      initGuestReviews(parseInt(params.hotelId!), cancelTokenSource.token);

      return () => {
        cancelTokenSource.cancel('Component unmount.');
      };
    }
  }, [params.hotelId, locale]);

  const triggerGenzoSearchesTrack = useCallback<(search: HotelSearchResultSearch) => void>(
    (search: HotelSearchResultSearch) => {
      const { id, country, city, region, checkIn, checkOut, guestsCount, roomsCount, hotel } =
        search;
      const now = new Date();

      const clientData: any = {
        id: wegoAnalyticsClientId,
        session_id: wegoAnalyticsClientSessionId,
        ...(!!user && { user_hash: user.userHash }),
      };

      const pageData: any = {
        name: `Hotels Results|${country.name}|${!!city ? city.name : ''}${
          !!region ? region.name : ''
        }`,
        base_type: 'hotels_search_results',
        locale: locale,
        site_code: currentSite.countryCode,
        url: window.location.href,
        referrer_url: document.referrer,
      };

      const searchData = {
        id: id,
        user: !!user ? { email: user.email } : null,
        client: {
          ...clientData,
          ...{
            app_type: 'WEB_APP',
            resolution: `${window.screen.availHeight}x${window.screen.availWidth}`,
          },
        },
        campaign: {
          sub_id: id,
          source: wegoSourceSearchParam,
          ts_code: getCookie('wego_ts_code') ?? null,
        },
        page: pageData,
        search: {
          ...(!!city && {
            city_code: city.code,
            city_name: city.name,
          }),
          id: id,
          check_in: checkIn,
          check_out: checkOut,
          country_code: country.code,
          country_name: country.name,
          guests_count: guestsCount,
          hotel_id: hotel!.id,
          rooms_count: roomsCount,
          site_code: currentSite.countryCode,
        },
        created_at: now.toISOString(),
        search_mode: 'MANUAL',
      };
      genzoTrack(`${apiBaseUrl}/genzo/v2/hotels/searches`, searchData);
    },
    [wegoAnalyticsClientId, wegoAnalyticsClientSessionId, user, locale, wegoSourceSearchParam],
  );

  const rateHandoffHref = useCallback<(rate: MetasearchHotelRate) => string>(
    (rate: MetasearchHotelRate) => {
      if (rate.provider!.directBooking) {
        return appendSearchParams(
          `${hotelsRoute(currentSite, localeParam)}/booking/checkout${searchWithoutExtraParams}`,
          { search_id: searchIdParam, reference_id: rate.id },
        );
      }
      return appendSearchParams(rate.handoffUrl, {
        ...additionalUrlTrackingParams,
        wc: wegoAnalyticsClientId,
        ws: wegoAnalyticsClientSessionId,
      });
    },
    [
      localeParam,
      searchWithoutExtraParams,
      searchIdParam,
      additionalUrlTrackingParams,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
    ],
  );

  const handleRateAmenityFilterToggle = useCallback<
    (rateAmenityId: number, isSelected: boolean) => void
  >(
    (rateAmenityId: number, isSelected: boolean) => {
      const filterRateEmenityElement = document.getElementById('RoomsDeals');
      // 164 is the height of sticky HotelAnchorTabs,
      // 68 is height of HotelStickyRatesFilter
      if (!!filterRateEmenityElement) {
        window.scrollTo({
          top: filterRateEmenityElement?.offsetTop - 164 - 68,
          behavior: 'smooth',
        });
      }

      const cloneSelectedRateAmenityIds = [...selectedRateAmenityIds];

      if (isSelected) {
        cloneSelectedRateAmenityIds.push(rateAmenityId);
      } else {
        cloneSelectedRateAmenityIds.splice(cloneSelectedRateAmenityIds.indexOf(rateAmenityId), 1);
      }

      const searchParams = new URLSearchParams(search);
      searchParams.delete('free_amenities');

      if (cloneSelectedRateAmenityIds.length > 0) {
        searchParams.append('free_amenities', cloneSelectedRateAmenityIds.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            impressionType: 'filter',
          },
        },
      );
    },
    [selectedRateAmenityIds, pathname, search, locationState],
  );

  const handleOnShowTotalPriceChanged = useCallback<(showTotalPrice: boolean) => void>(
    (showTotalPrice: boolean) => {
      dispatch(changeShowTotalPrice(showTotalPrice));
    },
    [],
  );

  const handleOnResetRateAmenities = useCallback(() => {
    const searchParams = new URLSearchParams(search);
    searchParams.delete('free_amenities');

    navigate(
      { pathname, search: searchParams.toString() },
      {
        replace: true,
        state: {
          ...locationState,
          impressionType: 'page_load',
        },
      },
    );
  }, [pathname, search, locationState]);

  // WebEngage Tracking for rate click
  const handleHotelDealsRateClick = useCallback<(selectedRate: MetasearchHotelRate) => void>(
    (selectedRate: MetasearchHotelRate) => {
      if (!!nearestCityByIP) {
        webEngageTrackRateClick(
          currentSite.countryCode,
          locale,
          nearestCityByIP,
          hotelMetaSearch.search!,
          window.location.href,
          hotel!,
          selectedRate,
        );
      }
      if (!selectedRate.provider.directBooking) {
        webEngageTrackPurchase();
      }
    },
    [locale, hotelMetaSearch.search?.id, nearestCityByIP, hotel, filteredRates],
  );

  const hotelDetailHref = useCallback<(hotel: SimilarHotel) => string>(
    (hotel: SimilarHotel) => {
      const pathname$ = pathname.replace(`/${params.hotelId}`, '');

      return appendSearchParams(
        `${window.location.origin}${pathname$}/${hotel.id}${searchWithoutExtraParams}`,
        { search_id: searchIdParam, wg_internal_link: 'similar_hotels' },
      );
    },
    [pathname, params.hotelId, searchWithoutExtraParams, searchIdParam],
  );

  const handleRateClick = useCallback(
    (rate: MetasearchHotelRate) => {
      if (
        roomSelectionRef.current &&
        hotelBookingRoomTypes?.data.rooms.find((room) => rate.params.room_type_id === room.typeId)
      ) {
        window.scrollTo({
          behavior: 'smooth',
          // 113 is the height of sticky search form,
          // 164 is the height of sticky HotelAnchorTabs,
          // 68 is height of HotelStickyRatesFilter
          // 48 is the marginTop of the <ChooseYourRoom /> component
          top: roomSelectionRef.current.offsetTop - 113 - 164 - 68 + 48,
        });
      } else {
        handleHotelDealsRateClick(rate);
        window.open(rateHandoffHref(rate), '_blank', 'noreferrer');
      }
    },
    [roomSelectionRef, hotelBookingRoomTypes],
  );

  const scrollDownSmoothly = (id: string) => {
    const searchLayoutHeight = document.getElementById('hotelSearchLayout')?.clientHeight || 0;
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const adjustedScrollPosition =
        targetElement.offsetTop - STICKY_BAR_HEIGHT - searchLayoutHeight!;

      window.scrollTo({
        top: adjustedScrollPosition,
        behavior: 'smooth',
      });
    }
  };

  function filterByRateAmenityIds(rates: MetasearchHotelRate[]) {
    return rates.filter((rate) => {
      const rateAmenityIdsFilter =
        selectedRateAmenityIds.length > 0
          ? selectedRateAmenityIds.every((selectedRateAmenityId) => {
              // Check rate for shopcash cashback rate amenity with id:10000
              if (selectedRateAmenityId === 10000) {
                return !!rate.params?.cashback_percentage;
              }

              // Check rate for BNPL rate amenity with id:10001
              if (selectedRateAmenityId === 10001) {
                const rateAmenities$ = rate.rateAmenityIds
                  .map((rateAmenityId) =>
                    rateAmenities.find((rateAmenity) => rateAmenityId === rateAmenity.id),
                  )
                  .filter(notUndefinedAndNull);

                return (
                  rate.provider.directBooking &&
                  !rateAmenities$.some(
                    (rateAmenity) =>
                      rateAmenity.code === 'PAY_AT_THE_HOTEL' || rateAmenity.code === 'PAY_LATER',
                  )
                );
              }

              return rate.rateAmenityIds.includes(selectedRateAmenityId);
            })
          : true;
      return rateAmenityIdsFilter;
    });
  }

  const decodeProviderSearchParam = (param: string | undefined | null): string | undefined => {
    return param ? window.atob(param) : undefined;
  };

  const filterRatesByProvider = (
    rates: MetasearchHotelRate[],
    providerCode: string | undefined,
    wegoHotelId: string | undefined | null,
  ): MetasearchHotelRate[] => {
    if (providerCode && wegoHotelId) {
      return rates.filter((rate) => rate.provider.code === providerCode);
    }
    return rates;
  };

  const getHotelRateAmount = (
    price: MetasearchHotelRatePrice,
    priceOptions: PriceOptions,
  ): number => {
    return convertHotelRatePriceToPrice(
      price,
      priceOptions.showTotalPrice,
      priceOptions.isIncludingTaxOrFee,
    ).amount;
  };

  const sortingRates = (
    rates: MetasearchHotelRate[],
    priceOptions: PriceOptions,
    sortDirection: 'asc' | 'desc',
  ): MetasearchHotelRate[] => {
    const sortedRates = rates.slice().sort((rateA, rateB) => {
      const amountA = getHotelRateAmount(rateA.price, priceOptions);
      const amountB = getHotelRateAmount(rateB.price, priceOptions);

      return sortDirection === 'asc' ? amountA - amountB : amountB - amountA;
    });

    return sortedRates;
  };

  const determineBestDeal = (
    rates: MetasearchHotelRate[],
    filteredRatesWithProvider: MetasearchHotelRate[],
    priceOptions: PriceOptions,
  ): MetasearchHotelRate | undefined => {
    if (rates.length === 0) return undefined;

    return filteredRatesWithProvider.length > 0
      ? sortingRates(filteredRatesWithProvider, priceOptions, 'asc')[0]
      : sortingRates(rates, priceOptions, 'asc')[0];
  };

  const handlePriceOptionsChanged = useCallback<(isIncludingTax: boolean) => void>(
    (isIncludingTax: boolean) => {
      dispatch(changeIsIncludingTaxOrFee(isIncludingTax));
    },
    [],
  );

  return (
    <>
      <Helmet>
        <title>{hotel?.name ? `${hotel.name} - ${title}` : title}</title>
      </Helmet>

      <div id='HotelInfoSection' className={styles.container}>
        <div className={styles.contentContainer}>
          <div className={styles.content}>
            <Breadcrumbs hotel={hotel} />
            {/* Hotel Name and Best Deal */}
            <div className={styles.hotelInformation}>
              <div className={styles.nameAndAddress}>
                <div className={styles.hotelName}>{hotel?.name}</div>

                <div className={styles.starAndAddress}>
                  {!!hotel?.star ? (
                    <div>
                      {[...Array(hotel.star).keys()].map((value: number) => {
                        return <StarIcon key={value} className={styles.star} />;
                      })}
                    </div>
                  ) : null}

                  <div className={styles.address}>
                    {[
                      hotel?.address?.trim(),
                      hotel?.district?.name,
                      hotel?.city.name !== hotel?.city.countryName ? hotel?.city.name : undefined,
                      !!hotel?.city.countryName && !!hotel?.postalCode
                        ? `${hotel.city.countryName} ${hotel.postalCode}`
                        : undefined,
                    ]
                      .filter(notUndefinedAndNull)
                      .join(', ')}
                  </div>
                </div>
              </div>

              {!!bestDeal ? (
                <div>
                  <div className={styles.bestDeal}>
                    <div>
                      <Price
                        className={styles.price}
                        currency={currency}
                        exchangeRate={exchangeRate}
                        isInline
                        locale={locale}
                        price={convertHotelRatePriceToPrice(
                          bestDeal.price,
                          priceOptions.showTotalPrice,
                          priceOptions.isIncludingTaxOrFee,
                        )}
                      />
                      {!!bestDeal.usualPrice && bestDealUsualPrice ? (
                        <div className={styles.discounted}>
                          <div className={styles.discountedPercentageV2}>{`${Math.round(
                            bestDeal.usualPrice.discountToUsualAmount * 100,
                          )}% off`}</div>
                          <Price
                            isInline
                            isStrikethrough
                            className={styles.usualPrice}
                            price={bestDealUsualPrice}
                            locale={locale}
                            currency={currency}
                            exchangeRate={exchangeRate}
                          />
                        </div>
                      ) : null}
                    </div>

                    {!bestDeal.provider!.directBooking ? (
                      <a
                        className={clsx(styles.viewDeal)}
                        href={rateHandoffHref(bestDeal)}
                        onClick={() => handleHotelDealsRateClick(bestDeal)}
                        target='_blank'
                        rel='noreferrer'
                      >
                        {translations.view_deal}
                      </a>
                    ) : (
                      <div className={styles.viewDeal} onClick={() => handleRateClick(bestDeal)}>
                        {hotelBookingRoomTypes?.data.rooms.find(
                          (room) => bestDeal.params.room_type_id === room.typeId,
                        ) ? (
                          <span>{translations.Select}</span>
                        ) : (
                          translations.book_now
                        )}
                      </div>
                    )}
                  </div>
                  <a
                    className={styles.provider}
                    href={rateHandoffHref(bestDeal)}
                    target='_blank'
                    rel='noreferrer'
                  >
                    {bestDeal.provider.name}
                    <OpenInNewIcon className={styles.icon} />
                  </a>
                </div>
              ) : null}
            </div>

            <HotelInformation
              pageViewId={pageViewId}
              hotel={hotel}
              allReviewerGroupReview={allReviewerGroupReview}
              bestDeal={bestDeal}
              hotelBookingRoomTypes={hotelBookingRoomTypes}
              rateHandoffHref={rateHandoffHref}
              handleRateClick={handleRateClick}
              handleHotelDealsRateClick={handleHotelDealsRateClick}
              scrollDownSmoothly={scrollDownSmoothly}
              wegoAnalyticsClientId={wegoAnalyticsClientId}
              wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
              setIsOpenMapPopup={setIsOpenMapPopup}
              isOpenMapPopup={isOpenMapPopup}
            />

            <HotelAnchorTabs />

            {/* Room Deals */}
            <div id='RoomsDeals' className={styles.categoryHeader}>
              {!isEnhancedHotelRates ? (
                translations.room_deals
              ) : (
                <>
                  <div className={styles.categoryHeaderTitle}>{translations.booking_options}</div>
                  <div className={styles.priceOptions}>
                    <div className={styles.checkboxWrapper}>
                      <Checkbox
                        className={styles.checkbox}
                        isChecked={priceOptions.isIncludingTaxOrFee}
                        onClick={() => handlePriceOptionsChanged(!priceOptions.isIncludingTaxOrFee)}
                      />

                      <div className={styles.labelWrapper}>
                        <div
                          className={styles.label}
                          title={translations.include_tax as string}
                          onClick={() =>
                            handlePriceOptionsChanged(!priceOptions.isIncludingTaxOrFee)
                          }
                        >
                          {translations.include_tax}
                        </div>
                      </div>
                    </div>

                    <div
                      className={styles.toggle}
                      onClick={() => handleOnShowTotalPriceChanged(!priceOptions.showTotalPrice)}
                    >
                      <div
                        className={clsx(
                          styles.option,
                          !priceOptions.showTotalPrice && styles.selected,
                        )}
                      >
                        {translations.hotel_per_night}
                      </div>
                      <div
                        className={clsx(
                          styles.option,
                          priceOptions.showTotalPrice && styles.selected,
                        )}
                      >
                        {translations.total_stay}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Rate Amenities Filter & Price Options */}
            <div id='RatesFilter' className={styles.rateAmenitiesFilterAndPriceOptions}>
              <div
                className={clsx(
                  styles.rateAmenitiesFilter,
                  isEnhancedHotelRates && styles.enhanced,
                )}
              >
                <div className={styles.rateAmenitiesFilterTitle}>
                  {translations.hotel_room_offer}
                </div>

                <div className={styles.rateAmenities}>
                  {filteredRateAmenities.map((rateAmenity) => (
                    <div
                      key={rateAmenity.id}
                      className={clsx(
                        styles.rateAmenity,
                        selectedRateAmenityIds.includes(rateAmenity.id) && styles.selected,
                      )}
                      onClick={() =>
                        handleRateAmenityFilterToggle(
                          rateAmenity.id,
                          !selectedRateAmenityIds.includes(rateAmenity.id),
                        )
                      }
                    >
                      {rateAmenity.name}
                    </div>
                  ))}
                </div>
              </div>

              {!isEnhancedHotelRates ? (
                <div className={styles.priceOptions}>
                  {translations.show_deals_by}

                  <div
                    className={styles.toggle}
                    onClick={() => handleOnShowTotalPriceChanged(!priceOptions.showTotalPrice)}
                  >
                    <div
                      className={clsx(
                        styles.option,
                        !priceOptions.showTotalPrice && styles.selected,
                      )}
                    >
                      {translations.hotel_per_night}
                    </div>
                    <div
                      className={clsx(
                        styles.option,
                        priceOptions.showTotalPrice && styles.selected,
                      )}
                    >
                      {translations.total_stay}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <HotelStickyRatesFilter
              filteredRateAmenities={filteredRateAmenities}
              selectedRateAmenityIds={selectedRateAmenityIds}
              handleRateAmenityFilterToggle={handleRateAmenityFilterToggle}
              handleOnShowTotalPriceChanged={handleOnShowTotalPriceChanged}
            />

            <HotelDeals
              additionalUrlTrackingParams={additionalUrlTrackingParams}
              className={styles.hotelDeals}
              hasTabbyGateway={hasTabbyGateway}
              hotel={hotel!}
              hotelBookingRoomTypes={hotelBookingRoomTypes}
              isGroupSearch={isGroupSearch}
              isLoadingRates={isLoadingRates}
              localeParam={localeParam}
              nearestCityByIP={nearestCityByIP!}
              onResetRateAmenities={handleOnResetRateAmenities}
              pageViewId={pageViewId}
              rateAmenities={filteredRateAmenities}
              rates={filteredRates}
              roomMap={roomMap}
              roomSelectionRef={roomSelectionRef}
              searchIdParam={searchIdParam}
              searchWithoutExtraParams={searchWithoutExtraParams}
              selectedRateAmenityIds={selectedRateAmenityIds}
              wegoAnalyticsClientId={wegoAnalyticsClientId}
              wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
            />

            {!!hotelBookingRoomTypes ? (
              <ChooseYourRoom
                currency={currency}
                currentSite={currentSite}
                exchangeRate={exchangeRate}
                hotelBookingRoomTypes={hotelBookingRoomTypes}
                hotelMetaSearch={hotelMetaSearch}
                locale={locale}
                localeParam={localeParam}
                priceOptions={priceOptions}
                rateAmenities={filteredRateAmenities}
                rates={filteredRates}
                ref={roomSelectionRef}
                searchIdParam={searchIdParam}
                searchWithoutExtraParams={searchWithoutExtraParams}
                selectedRateAmenityIds={selectedRateAmenityIds}
                translations={translations}
              />
            ) : null}

            {/* Guest Rating */}
            {!!hotel?.reviews && hotel.reviews.length > 0 ? (
              <div id='GuestRating' className={styles.guestRatingWrapper}>
                <div className={styles.categoryHeader}>{translations.guest_rating}</div>

                <AiReviews
                  className={styles.aiReviews}
                  reviews={hotel.reviews}
                  aiReviews={hotel.aiReviews}
                />
              </div>
            ) : null}

            <GuestReviews reviewLocale={reviewLocale} reviews={reviews} />

            {/* Map */}
            {!!hotel?.latitude && !!hotel?.longitude ? (
              <div id='Map' className={styles.w100}>
                <div className={styles.categoryHeader}>{translations.hotel_results_map}</div>

                <div ref={mapDivRef} className={styles.map}></div>
              </div>
            ) : null}

            {/* Similar Hotels */}
            {similarHotelViewModels.length > 0 ? (
              <div id='SimilarHotels' className={styles.w100}>
                <div className={styles.categoryHeader}>{translations.similar_hotels_title}</div>

                <Carousel
                  className={styles.similarHotels}
                  isRtl={isRtl}
                  itemLength={similarHotelViewModels.length}
                  itemWidth={224}
                  scrollWidth={224}
                >
                  {similarHotelViewModels.map((similarHotelViewModel) => (
                    <HotelMiniCard
                      href={hotelDetailHref(similarHotelViewModel.hotel)}
                      key={similarHotelViewModel.hotel.id}
                      similarHotelViewModel={similarHotelViewModel}
                    />
                  ))}
                </Carousel>
              </div>
            ) : null}

            {/* Details */}
            {!!hotel ? (
              <div id='Details'>
                <div className={styles.categoryHeader}>{translations.details}</div>

                <HotelDescription className={styles.details} hotel={hotel} />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {isSearchExpired ? (
        <RefreshPopup
          genzoTrackPageViewBaseType='hotels_details'
          product={ProductTypes.HOTELS}
          localeParam={localeParam}
          onRefresh={() => {
            searchParams.delete('search_id');
            navigate(
              { pathname, search: searchParams.toString() },
              { replace: true, state: locationState },
            );
          }}
          onClose={() => setIsSearchExpired(false)}
          wegoAnalyticsClientId={wegoAnalyticsClientId}
          wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
        />
      ) : null}
    </>
  );
};

export default HotelDetail;
